<template>
  <div class="bg-white dark:bg-dark-box-background shadow-sm rounded-lg p-6">
    <div class="space-y-6">
      <!-- Header -->
      <div class="flex justify-between items-center">
        <h2 class="text-lg font-medium text-gray-900 dark:text-white">
          {{ isEditing ? $t("Edit Object Type") : $t("Add Object Type") }}
        </h2>
        <button class="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300" @click="$emit('cancel')">
          <X class="w-5 h-5" />
        </button>
      </div>

      <!-- Form -->
      <Form v-slot="{values}" :validation-schema="schema" :initial-values="initial_values" @submit="handleSubmit">
        <div class="space-y-6">
          <!-- Label (Singular) -->
          <div>
            <div class="mt-1">
              <TextInput
                id="label"
                name="label"
                type="text"
                :placeholder="$t('e.g., Company, Contact, Department')"
                :label="$t('Label (Singular)')"
                :show-label="true" />
            </div>
            <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">
              {{ $t("This is how the object type will be displayed in singular form") }}
            </p>
          </div>
          <div>
            <div class="mt-1">
              <TextInput
                id="type"
                name="type"
                :value="isEditing ? objectType.type:utils.slugify(values.label)"
                :disabled="true"
                type="text"
                :label="$t('Type (Read Only)')"
                :show-label="true" />
            </div>
            <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">
              {{ $t("This is the unique identifier of the object type. It is generated from the label (singular) and cannot be changed.") }}
            </p>
          </div>

          <!-- Label (Plural) -->
          <div>
            <div class="mt-1">
              <TextInput
                id="plural_label"
                name="plural_label"
                type="text"
                :value="!isEditing&&values.label ? utils.nounHelper.toPlural(values.label) : values.plural_label"
                :placeholder="$t('e.g., Companies, Contacts, Departments')"
                :label="$t('Label (Plural)')"
                :show-label="true" />
            </div>
            <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">
              {{ $t("This is how the object type will be displayed in plural form") }}
            </p>
          </div>

          <!-- Description -->
          <div>
            <div class="mt-1">
              <TextArea
                class="mt-4"
                name="description"
                :label="$t('Description')"
                :placeholder="
                  $t(
                    'e.g., A company is a legal entity made up of an association of people, be they natural, legal, or a mixture of both, for carrying on a commercial or industrial enterprise.'
                  )
                "
                :show-label="true" />
            </div>
          </div>

          <!-- Actions -->
          <div class="flex justify-end space-x-3">
            <button type="button" class="btn-cancel" @click="$emit('cancel')">
              {{ $t("Cancel") }}
            </button>
            <button type="submit" :disabled="loading" class="btn-primary w-fit">
              <template v-if="loading">
                <div class="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                {{ $t("Saving...") }}
              </template>
              <template v-else>
                {{ isEditing ? $t("Update") : $t("Create") }}
              </template>
            </button>
          </div>
        </div>
      </Form>
    </div>
  </div>
</template>

<script>
import { Form } from "vee-validate"
import { X } from "lucide-vue-next"
import TextInput from "@/components/shared/TextInput.vue"
import TextArea from "@/components/shared/TextArea.vue"
import * as yup from "yup"
import { utils } from "@/store/helpers"

export default {
  name: "ObjectTypeForm",
  components: {
    X,
    Form,
    TextArea,
    TextInput
  },
  props: {
    objectType: {
      type: Object,
      default: () => ({})
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  emits: ["submit", "cancel"],
  data() {
    return {
      utils,
      initial_values: {
        label: "",
        plural_label: "",
        description: "",
        type: ""
      },
      schema: yup.object({
        label: yup.string().required(this.$t("Label is required")),
        plural_label: yup.string().required(this.$t("Plural label is required")),
        description: yup.string(),
        type: yup.string()
      })
    }
  },
  computed: {
    isEditing() {
      return this.objectType?.id
    },
  },
  created () {
    if (this.objectType?.id) {
      this.initial_values = {
          label: this.objectType.label || "",
          plural_label: this.objectType.plural_label || "",
          description: this.objectType.description || "",
          type: this.objectType.type || ""
        }
      }
  },
  methods: {
    async handleSubmit(values) {
      if (this.isEditing) {
        const payload = {
          ...values,
          id: this.objectType.id
        }
        this.$store
          .dispatch("objects/updateObjectType", payload)
          .then(() => {
            this.$toast.open({
              message: this.$t("Object type updated successfully"),
              position: "top",
              type: "success",
              duration: 5000
            })
            this.$emit("submit")
          })
          .catch((err) => {
            console.error(err)
            this.$toast.open({
              message: `Failed to update object type ${err?.data?.message}`,
              position: "top",
              type: "error",
              duration: 5000
            })
          })
      } else {
        const payload = {
          ...values,
          integration: "platform"
        }
        this.$store.dispatch("objects/addObjectType", payload).then(() => {
          this.$toast.open({
            message: this.$t("Object type created successfully"),
            position: "top",
            type: "success",
            duration: 5000
          })
          this.$emit("submit")
        })
      }
    }
  }
}
</script>
